package diana.soleil.design.iraniancanadienbazzar.feature.addbusiness

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.koin.compose.viewmodel.koinViewModel

/**
 * Host for the whole Add / Claim business wizard: one nav destination, internal [WizardStep] state so
 * the draft survives step navigation. [onExit] pops the destination (from the intro back button, a
 * Cancel/Save Draft, or the Done button on the post-submit confirmation); [onOpenSearch] backs the
 * "claim an existing listing" search. Finishing on Free submits the draft as a pending
 * `businesses/{id}` doc awaiting admin approval, then uploads the picked media and writes the
 * drafted products (both best-effort — see AddBusinessViewModel.submitFree).
 */
@Composable
fun AddBusinessRoute(
    onExit: () -> Unit,
    onOpenSearch: (query: String) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: AddBusinessViewModel = koinViewModel(),
) {
    val step by viewModel.step.collectAsStateWithLifecycle()
    val draft by viewModel.draft.collectAsStateWithLifecycle()
    val categories by viewModel.categories.collectAsStateWithLifecycle()
    val submitState by viewModel.submitState.collectAsStateWithLifecycle()
    val hasSavedDraft by viewModel.hasSavedDraft.collectAsStateWithLifecycle()

    val onStepBack: () -> Unit = { if (!viewModel.back()) onExit() }

    when (step) {
        WizardStep.Intro -> AddBusinessIntroScreen(
            onBack = onExit,
            onAddBusiness = viewModel::startNewBusiness,
            onSearchRegistry = onOpenSearch,
            onLearnMore = { viewModel.goTo(WizardStep.Plan) },
            modifier = modifier,
            hasSavedDraft = hasSavedDraft,
            onResumeDraft = viewModel::startNewBusiness,
            onDiscardDraft = viewModel::discardDraft,
        )
        WizardStep.Info -> AddBusinessInfoScreen(
            draft = draft,
            categories = categories,
            onName = viewModel::setName,
            onCategory = viewModel::setCategory,
            onToggleAttribute = viewModel::toggleAttribute,
            onDescription = viewModel::setDescription,
            onBack = onStepBack,
            onCancel = onExit,
            onNext = viewModel::next,
            modifier = modifier,
        )
        WizardStep.Location -> AddBusinessLocationScreen(
            draft = draft,
            onAddressLine = viewModel::setAddressLine,
            onCity = viewModel::setCity,
            onProvince = viewModel::setProvince,
            onPostalCode = viewModel::setPostalCode,
            onBack = onStepBack,
            onNext = viewModel::next,
            modifier = modifier,
        )
        WizardStep.Photos -> AddBusinessPhotosScreen(
            draft = draft,
            onPickCover = viewModel::pickCover,
            onRemoveCover = viewModel::removeCover,
            onPickLogo = viewModel::pickLogo,
            onRemoveLogo = viewModel::removeLogo,
            onAddGalleryPhoto = viewModel::addGalleryPhoto,
            onRemoveGalleryPhoto = viewModel::removeGalleryPhoto,
            onSaveDraft = onExit,
            onBack = onStepBack,
            onNext = viewModel::next,
            modifier = modifier,
        )
        WizardStep.Products -> AddBusinessProductsScreen(
            draft = draft,
            onAddProduct = viewModel::addProduct,
            onRemoveProduct = viewModel::removeProduct,
            onBack = onStepBack,
            onNext = viewModel::next,
            modifier = modifier,
        )
        WizardStep.Plan -> AddBusinessPlanScreen(
            draft = draft,
            submitting = submitState.submitting,
            submitStatus = submitState.status,
            partialUploadFailed = submitState.partialUploadFailed,
            // Free is the completing action (paid plans are interest capture until payments exist):
            // it persists the draft as a pending `businesses/{id}` doc; success shows the in-wizard
            // confirmation whose Done button exits, failures render inline on this screen.
            onContinueFree = { viewModel.submitFree() },
            onRegisterInterest = viewModel::registerPlanInterest,
            onDone = onExit,
            onBack = onStepBack,
            modifier = modifier,
            duplicateName = submitState.duplicateName,
            onSubmitAnyway = { viewModel.submitFree(force = true) },
            onDismissDuplicate = viewModel::dismissDuplicate,
        )
    }
}
